import { useState, useEffect } from 'react';
import { useAuth } from '@qwickapps/auth-client';
import { Text } from '@qwickapps/react-framework';

import AgentStatusCard from '../components/dashboard/AgentStatusCard';
import AgentList from '../components/dashboard/AgentList';
import AgentActivityPanel from '../components/dashboard/AgentActivityPanel';
import GoalsSummary from '../components/dashboard/GoalsSummary';
import GoalsItemsPanel from '../components/dashboard/GoalsItemsPanel';
import KanbanBoard from '../components/dashboard/KanbanBoard';
import InfrastructurePanel from '../components/dashboard/InfrastructurePanel';
import CostOverview from '../components/dashboard/CostOverview';
import DocumentSearch from '../components/dashboard/DocumentSearch';
import PROverview from '../components/dashboard/PROverview';
import SystemGlobe from '../components/dashboard/SystemGlobe';
import LogsPanel from '../components/dashboard/LogsPanel';
import DashboardStats from '../components/dashboard/DashboardStats';
import CICDStatusPanel from '../components/dashboard/CICDStatusPanel';
import TimelineView from '../components/dashboard/TimelineView';
import DependencyGraph from '../components/dashboard/DependencyGraph';
import { useAgents } from './useAgents';
import { useGoals, useGoalItems } from '../hooks/useProjectsApi';

type DashboardView = 'overview' | 'agents' | 'goals' | 'infrastructure' | 'costs' | 'documents' | 'code-review' | 'logs' | 'ci' | 'timeline' | 'dependency-graph';

const STYLES = `
  @keyframes sdlc-spin { to { transform: rotate(360deg); } }
  .sdlc-spinner {
    width: 40px; height: 40px;
    border: 3px solid var(--theme-border);
    border-top-color: var(--theme-primary);
    border-radius: 50%;
    animation: sdlc-spin 1s linear infinite;
  }
  .sdlc-root {
    height: calc(100vh - 64px);
    display: flex;
    flex-direction: row;
  }
  .sdlc-mobile-toggle {
    display: none;
    padding: 8px;
    border-bottom: 1px solid var(--theme-border);
    background: var(--theme-surface);
  }
  .sdlc-mobile-toggle-btn {
    padding: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    color: var(--theme-text);
  }
  .sdlc-sidebar {
    width: 240px;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    border-right: 1px solid var(--theme-border);
    padding: 16px;
    background: var(--theme-surface);
    overflow-y: auto;
  }
  .sdlc-sidebar-nav {
    margin-top: 24px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .sdlc-sidebar-item {
    padding: 12px;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: background 0.2s, color 0.2s;
    color: var(--theme-text);
    background: transparent;
    border: none;
    width: 100%;
    text-align: left;
  }
  .sdlc-sidebar-item:hover { background: var(--theme-surface-hover); }
  .sdlc-sidebar-item.active { background: var(--theme-primary); color: white; }
  .sdlc-sidebar-item.active:hover { background: var(--theme-primary); }
  .sdlc-main { flex: 1; padding: 24px; overflow: auto; }
  .sdlc-overview-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }
  .sdlc-overview-grid .span-full { grid-column: span 3; }
  @media (max-width: 899px) {
    .sdlc-root { flex-direction: column; }
    .sdlc-mobile-toggle { display: flex; }
    .sdlc-sidebar {
      width: 100%;
      flex-direction: row;
      flex-wrap: wrap;
      border-right: none;
      border-bottom: 1px solid var(--theme-border);
    }
    .sdlc-sidebar.closed { display: none; }
    .sdlc-sidebar-nav { flex-direction: row; flex-wrap: wrap; margin-top: 8px; }
    .sdlc-overview-grid { grid-template-columns: 1fr 1fr; }
    .sdlc-overview-grid .span-full { grid-column: span 2; }
  }
  @media (max-width: 599px) {
    .sdlc-overview-grid { grid-template-columns: 1fr; }
    .sdlc-overview-grid .span-full { grid-column: span 1; }
  }
`;

export default function SDLCDashboardPage() {
  const { session } = useAuth();
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState<DashboardView>('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [kanbanGoalId, setKanbanGoalId] = useState<string | undefined>(undefined);
  const { agents: agentData, loading: agentsLoading } = useAgents();

  useEffect(() => {
    if (!session?.accessToken) {
      setLoading(false);
      return;
    }

    setLoading(false);
  }, [session?.accessToken]);

  if (loading) {
    return (
      <div style={{ padding: '64px 0', display: 'flex', justifyContent: 'center' }}>
        <style>{STYLES}</style>
        <div className="sdlc-spinner" />
      </div>
    );
  }

  // Called when a goal card is clicked in GoalsSummary — navigate to kanban pre-filtered by that goal
  const handleGoalCardClick = (goalId: string) => {
    setKanbanGoalId(goalId);
    setCurrentView('goals');
    if (window.matchMedia('(max-width: 899px)').matches) {
      setSidebarOpen(false);
    }
  };

  const renderContent = () => {
    switch (currentView) {
      case 'overview':
        return (
          <div className="sdlc-overview-grid">
            <div className="span-full">
              <DashboardStats />
            </div>
            <div className="span-full">
              <SystemGlobe />
            </div>
            <GoalsSummary onGoalClick={handleGoalCardClick} />
            <CostOverview />
            <CICDStatusPanel />
            <div className="span-full">
              <Text variant="h6" content="Active Agents" />
              <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
                {agentsLoading && (
                  <Text variant="body2" content="Loading agents…" customColor="var(--theme-text-secondary)" />
                )}
                {!agentsLoading && (agentData ?? []).map((agent) => (
                  <AgentStatusCard key={agent.id} agent={agent} />
                ))}
                {!agentsLoading && (agentData ?? []).length === 0 && (
                  <Text variant="body2" content="No active agents found." customColor="var(--theme-text-secondary)" />
                )}
              </div>
            </div>
          </div>
        );
      case 'agents':
        return (
          <div>
            <Text variant="h5" content="Agent Status" gutterBottom />
            <div style={{ marginTop: 16 }}>
              <AgentList />
            </div>
            <div style={{ marginTop: 32 }}>
              <AgentActivityPanel />
            </div>
          </div>
        );
      case 'goals':
        return (
          <div>
            <GoalsSummary />
            <div style={{ marginTop: 32 }}>
              <GoalsItemsPanel />
            </div>
            <div style={{ marginTop: 32 }}>
              <Text variant="h5" content="Task Board" gutterBottom />
              <KanbanBoard initialGoalId={kanbanGoalId} />
            </div>
          </div>
        );
      case 'ci':
        return <CICDStatusPanel />;
      case 'infrastructure':
        return <InfrastructurePanel />;
      case 'costs':
        return <CostOverview />;
      case 'documents':
        return <DocumentSearch />;
      case 'code-review':
        return <PROverview />;
      case 'logs':
        return <LogsPanel />;
      case 'timeline':
        return <TimelineView height={500} />;
      case 'dependency-graph':
        return <DependencyGraphContainer />;
      default:
        return null;
    }
  };

  return (
    <>
      <style>{STYLES}</style>
      {/* Mobile toggle */}
      <div className="sdlc-mobile-toggle">
        <button
          className="sdlc-mobile-toggle-btn"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          <span className="material-icons">{sidebarOpen ? 'menu_open' : 'menu'}</span>
          <Text variant="body2" content="Menu" />
        </button>
      </div>

      <div className="sdlc-root">
        {/* Sidebar */}
        <nav
          data-testid="sdlc-sidebar"
          className={`sdlc-sidebar${sidebarOpen ? '' : ' closed'}`}
        >
          <Text variant="h6" content="SDLC Dashboard" gutterBottom customColor="var(--theme-primary)" />
          <div className="sdlc-sidebar-nav">
            <SidebarItem label="Overview" icon="dashboard" active={currentView === 'overview'} onClick={() => setCurrentView('overview')} />
            <SidebarItem label="Agents" icon="group" active={currentView === 'agents'} onClick={() => setCurrentView('agents')} />
            <SidebarItem label="Goals & Tasks" icon="flag" active={currentView === 'goals'} onClick={() => setCurrentView('goals')} />
            <SidebarItem label="Infrastructure" icon="cloud" active={currentView === 'infrastructure'} onClick={() => setCurrentView('infrastructure')} />
            <SidebarItem label="Costs" icon="attach_money" active={currentView === 'costs'} onClick={() => setCurrentView('costs')} />
            <SidebarItem label="Documents" icon="description" active={currentView === 'documents'} onClick={() => setCurrentView('documents')} />
            <SidebarItem label="Code Review" icon="code" active={currentView === 'code-review'} onClick={() => setCurrentView('code-review')} />
            <SidebarItem label="Logs" icon="list_alt" active={currentView === 'logs'} onClick={() => setCurrentView('logs')} />
            <SidebarItem label="CI/CD" icon="rocket_launch" active={currentView === 'ci'} onClick={() => setCurrentView('ci')} />
            <SidebarItem label="Timeline" icon="timeline" active={currentView === 'timeline'} onClick={() => setCurrentView('timeline')} />
            <SidebarItem label="Dependency Graph" icon="account_tree" active={currentView === 'dependency-graph'} onClick={() => setCurrentView('dependency-graph')} />
          </div>
        </nav>

        {/* Main Content */}
        <main className="sdlc-main">
          {renderContent()}
        </main>
      </div>
    </>
  );
}

/**
 * Container that fetches items from active goals and passes them to DependencyGraph.
 * Uses the first active goal's items for the dependency visualization.
 */
function DependencyGraphContainer() {
  const { data: goalsData, loading: goalsLoading } = useGoals('active');
  const firstGoalId = goalsData?.goals?.[0]?.id ?? null;
  const { data: itemsData, loading: itemsLoading } = useGoalItems(firstGoalId);

  if (goalsLoading || itemsLoading) {
    return (
      <div style={{ padding: '64px 0', display: 'flex', justifyContent: 'center' }}>
        <div className="sdlc-spinner" />
      </div>
    );
  }

  const items = itemsData?.items ?? [];
  return <DependencyGraph items={items} height={500} />;
}

interface SidebarItemProps {
  label: string;
  icon: string;
  active: boolean;
  onClick: () => void;
}

function SidebarItem({ label, icon, active, onClick }: SidebarItemProps) {
  return (
    <button
      onClick={onClick}
      className={`sdlc-sidebar-item${active ? ' active' : ''}`}
    >
      <span className="material-icons" style={{ fontSize: 18 }}>{icon}</span>
      <Text variant="body2" content={label} />
    </button>
  );
}
